"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/*
 * Copyright 2026, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const react_1 = __importStar(require("react"));
const maplibre_1 = __importStar(require("react-map-gl/maplibre"));
require("maplibre-gl/dist/maplibre-gl.css");
const react_i18next_1 = require("react-i18next");
const ServiceLocator_1 = __importDefault(require("chaire-lib-common/lib/utils/ServiceLocator"));
const ConfirmModal_1 = __importDefault(require("chaire-lib-frontend/lib/components/modal/ConfirmModal"));
const usePreference_1 = require("chaire-lib-frontend/lib/hooks/usePreference");
const mapBaseLayersProject_types_1 = require("chaire-lib-common/lib/config/mapBaseLayersProject.types");
const TransitionMapControlsMenu_1 = __importDefault(require("./TransitionMapControlsMenu"));
const projectBaseMapLayers_1 = require("../../config/projectBaseMapLayers");
/**
 * Functional component wrapper for MapLibreMap to handle hooks.
 * This component manages the map view state, the custom layers animation, and renders
 * the MapLibre map with all necessary controls and overlays.
 */
const MapRenderer = ({ mapRef, defaultCenter, defaultZoom, mapLoaded, getMapStyle, setAnimationEnabled, setupMapEvents, setMap, confirmModalDeleteIsOpen, activeBasemapShortname, currentZoom, overlayOpacity, overlayColor, maxRasterBasemapZoom, handleZoomChange, onLayerChange, onOverlayOpacityChange, onOverlayColorChange, onDeleteSelectedNodes, closeModal, children }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const handleResetView = react_1.default.useCallback(() => {
        var _a, _b;
        (_b = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap()) === null || _b === void 0 ? void 0 : _b.flyTo({
            center: defaultCenter,
            zoom: defaultZoom,
            bearing: 0,
            pitch: 0
        });
    }, [mapRef, defaultCenter, defaultZoom]);
    // Initial camera only. The map keeps it afterwards. Passing longitude/zoom back as props
    // makes react-map-gl replace MapLibre's in-progress gesture with the previous React render,
    // so the custom layers and the GeoJSON layers are no longer drawn at the same zoom.
    const initialViewState = (0, react_1.useMemo)(() => ({
        longitude: defaultCenter[0],
        latitude: defaultCenter[1],
        zoom: defaultZoom
    }), [defaultCenter, defaultZoom]);
    const [enableAnimation] = (0, usePreference_1.usePreference)('map.enableAnimation', true);
    (0, react_1.useEffect)(() => {
        setAnimationEnabled(enableAnimation);
    }, [enableAnimation, setAnimationEnabled]);
    // Initial style for <MapLibreMap>; subsequent changes go through setStyle imperatively.
    const [initialMapStyle] = (0, react_1.useState)(() => getMapStyle());
    /**
     * Ref that tracks which basemap shortname was last applied via setStyle,
     * so we can skip no-op overlay-only updates when the basemap hasn't changed.
     */
    const appliedBasemapRef = (0, react_1.useRef)(null);
    /** Cached style JSON keyed by basemap shortname so overlay-only changes skip the network. */
    const fetchedStyleCacheRef = (0, react_1.useRef)(null);
    // Unified basemap + overlay style effect.
    // Uses MapLibre's `setStyle` with `diff: true` for same-basemap overlay tweaks (only changed
    // sources/layers are patched), and `diff: false` for basemap switches (full style replacement).
    // See https://maplibre.org/maplibre-gl-js/docs/API/classes/Map/#setstyle
    // The AbortController cancels in-flight styleUrl fetches when deps change before the response
    // arrives (standard React effect cleanup pattern for async requests).
    (0, react_1.useEffect)(() => {
        var _a;
        const map = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
        if (!map || !mapLoaded)
            return;
        const projectBasemap = (0, projectBaseMapLayers_1.getProjectBasemapByShortname)(activeBasemapShortname);
        const basemapChanged = appliedBasemapRef.current !== activeBasemapShortname;
        if (projectBasemap && (0, mapBaseLayersProject_types_1.isProjectBasemapStyleUrl)(projectBasemap)) {
            const requested = activeBasemapShortname;
            const applyStyle = (style) => {
                var _a;
                const m = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.getMap();
                if (!m)
                    return;
                const switchingBasemap = appliedBasemapRef.current !== requested;
                const composed = (0, projectBaseMapLayers_1.composeMapStyleWithOverlay)(style, overlayOpacity, overlayColor);
                m.setStyle(composed, { diff: !switchingBasemap });
                appliedBasemapRef.current = requested;
            };
            const cached = fetchedStyleCacheRef.current;
            if (cached && cached.shortname === requested && !basemapChanged) {
                applyStyle(cached.style);
                return;
            }
            const { styleUrl } = projectBasemap;
            const ac = new AbortController();
            if (basemapChanged) {
                ServiceLocator_1.default.eventManager.emit('progress', { name: 'MapStyleSwitch', progress: 0.0 });
            }
            fetch(styleUrl, { signal: ac.signal })
                .then((r) => r.json())
                .then((style) => {
                fetchedStyleCacheRef.current = { shortname: requested, style };
                applyStyle(style);
            })
                .catch((e) => {
                if (e.name !== 'AbortError') {
                    console.error('Style basemap load failed:', e);
                    ServiceLocator_1.default.eventManager.emit('progressClear', { name: 'MapStyleSwitch' });
                }
            });
            return () => ac.abort();
        }
        if (projectBasemap) {
            if (basemapChanged) {
                ServiceLocator_1.default.eventManager.emit('progress', { name: 'MapStyleSwitch', progress: 0.0 });
            }
            const composed = (0, projectBaseMapLayers_1.composeMapStyleWithOverlay)(projectBasemap.style, overlayOpacity, overlayColor);
            map.setStyle(composed, { diff: !basemapChanged });
            appliedBasemapRef.current = activeBasemapShortname;
            return;
        }
        // OSM raster fallback
        if (basemapChanged) {
            ServiceLocator_1.default.eventManager.emit('progress', { name: 'MapStyleSwitch', progress: 0.0 });
        }
        const style = getMapStyle();
        map.setStyle(style, { diff: !basemapChanged });
        appliedBasemapRef.current = activeBasemapShortname;
    }, [activeBasemapShortname, overlayOpacity, overlayColor, mapLoaded, getMapStyle]);
    return (react_1.default.createElement("section", { id: "tr__main-map" },
        react_1.default.createElement("div", { id: "tr__main-map-context-menu", className: "tr__main-map-context-menu" }),
        children,
        react_1.default.createElement(maplibre_1.default, { ref: mapRef, initialViewState: initialViewState, onMove: (evt) => {
                // Panning reports the same zoom. Updating state then re-renders the map for no basemap change.
                if (evt.viewState.zoom !== currentZoom) {
                    handleZoomChange(evt.viewState.zoom);
                }
            }, onLoad: () => {
                setMap();
                setupMapEvents();
            }, 
            // Keep OSM and MapLibre credits expanded so map screenshots include them.
            attributionControl: { compact: false }, style: { width: '100%', height: '100%' }, maxZoom: Math.max(20, maxRasterBasemapZoom), mapStyle: initialMapStyle, hash: true },
            react_1.default.createElement(maplibre_1.ScaleControl, { position: "bottom-right" }),
            react_1.default.createElement(TransitionMapControlsMenu_1.default, { currentLayer: activeBasemapShortname, currentZoom: currentZoom, overlayOpacity: overlayOpacity, overlayColor: overlayColor, onLayerChange: onLayerChange, onOverlayOpacityChange: onOverlayOpacityChange, onOverlayColorChange: onOverlayColorChange, onResetView: handleResetView })),
        confirmModalDeleteIsOpen && (react_1.default.createElement(ConfirmModal_1.default, { title: t('transit:transitNode:ConfirmMultipleDelete'), confirmAction: onDeleteSelectedNodes, isOpen: true, confirmButtonColor: "red", confirmButtonLabel: t('transit:transitNode:MultipleDelete'), closeModal: closeModal }))));
};
exports.default = MapRenderer;
//# sourceMappingURL=MapRenderer.js.map